import type { PurchaseJobPlBucket, PurchaseRequisition } from '../types/purchasing';
import type { EnhancedDeal, TimberProject } from '../types';
import { isAchPurchasePayment } from './purchasingPolicy';
import {
  isDealInDefaultBillingProjectScope,
  OVERHEAD_BILLING_DEAL_ID,
} from './projectBillingSummary';
import type { SupplierBillCategory, SupplierPaymentTermsSpec } from './supplierBills';

export function billCategoryForJobPlBucket(
  bucket: PurchaseJobPlBucket | string | undefined,
): SupplierBillCategory {
  // The timber node on a job PO is other materials (decking, hardware), not the timber package.
  if (bucket === 'timber') return 'other';
  if (bucket === 'production') return 'cnc';
  if (bucket === 'freight') return 'freight';
  return 'other';
}

export function shouldCreatePayableForIssuedPurchase(
  method: { id?: string; label?: string } | string | null | undefined,
): boolean {
  return isAchPurchasePayment(method);
}

/** Bill Payment POs close from Billing & Payables, not the card receipt step. */
export function purchaseFollowsSupplierBill(
  method: { id?: string; label?: string } | string | null | undefined,
): boolean {
  return isAchPurchasePayment(method);
}

export const BILL_PAYMENT_PURCHASE_STEP_NOTE =
  'Pay this bill in Billing & Payables. This PO closes when the final payment is marked paid.';

export const DESIGNER_INVOICE_BILL_PAYMENT_STEP_NOTE =
  'Pay through the designer invoice QuickBooks bill (Project P&L → Designer invoices). This PO tracks job cost and closes when that bill is paid.';

export const PRECON_BILL_PAYMENT_STEP_NOTE =
  'Billing & Payables is for under-contract jobs only. Create and pay the vendor bill in QuickBooks, then mark this PO purchased here so it closes.';

export const DESIGNER_INVOICE_REVIEW_PATH = '/finances/project-pl?review=designer-invoices';

export type BillPaymentPurchaseUiGuide = {
  stepNote: string;
  /** When set, show the Billing & Payables deep link. */
  billingPayablesHref: string | null;
  /** Secondary navigation when B&P is not the right surface. */
  secondaryLink?: { label: string; to: string };
};

/** True when the UI offers a Bill Payment close path (B&P or designer-invoice). */
export function billPaymentPurchaseHasUiClosePath(
  guide: BillPaymentPurchaseUiGuide | null | undefined,
): boolean {
  if (!guide) return false;
  return Boolean(guide.billingPayablesHref || guide.secondaryLink);
}

export function billPaymentPurchaseUiGuide(args: {
  paymentMethod: { id?: string; label?: string } | string | null | undefined;
  designerInvoiceId?: string | null;
  dealId?: string | null;
  /** CRM supplier bill id — deep-links Billing & Payables to the right row. */
  supplierBillId?: string | null;
  timberProjects: TimberProject[];
  deals: EnhancedDeal[];
}): BillPaymentPurchaseUiGuide | null {
  if (!purchaseFollowsSupplierBill(args.paymentMethod)) return null;

  const dealId = args.dealId?.trim() || '';
  const billId = args.supplierBillId?.trim() || '';
  const billQs = billId ? `&bill=${encodeURIComponent(billId)}` : '';

  if (args.designerInvoiceId?.trim()) {
    return {
      stepNote: DESIGNER_INVOICE_BILL_PAYMENT_STEP_NOTE,
      billingPayablesHref: dealId
        ? `/billing-payables?deal=${encodeURIComponent(dealId)}${billQs}`
        : (billId ? `/billing-payables?bill=${encodeURIComponent(billId)}` : null),
      secondaryLink: { label: 'Open designer bills', to: '/finances?hub=billings&tab=designer-open' },
    };
  }

  const inBillingScope =
    dealId &&
    isDealInDefaultBillingProjectScope(dealId, args.timberProjects, args.deals);

  if (dealId && !inBillingScope) {
    return {
      stepNote: PRECON_BILL_PAYMENT_STEP_NOTE,
      billingPayablesHref: null,
    };
  }

  return {
    stepNote: BILL_PAYMENT_PURCHASE_STEP_NOTE,
    billingPayablesHref: dealId
      ? `/billing-payables?deal=${encodeURIComponent(dealId)}${billQs}`
      : `/billing-payables?deal=${encodeURIComponent(OVERHEAD_BILLING_DEAL_ID)}${billQs}`,
  };
}

/** True when every non-void bill on the PO is paid (the final payment). */
export function supplierBillPaymentClosesPurchase(statuses: Array<string | null | undefined>): boolean {
  const active = statuses.filter((status) => status && status !== 'void');
  return active.length > 0 && active.every((status) => status === 'paid');
}

export function shouldAwardLinkedOtherQuote(
  row: Pick<PurchaseRequisition, 'rfqSubmissionId' | 'rfqProjectId'>,
): boolean {
  return !!row.rfqSubmissionId?.trim() && !!row.rfqProjectId?.trim();
}

/** Skip RFQ-award bills when the quote already has a job PO. */
export function shouldSkipRfqAwardBill(submission: {
  purchaseRequisitionId?: string | null;
}): boolean {
  return typeof submission.purchaseRequisitionId === 'string'
    && submission.purchaseRequisitionId.trim().length > 0;
}

export function buildIssuedPurchasePayableInput(args: {
  requisition: PurchaseRequisition;
  poNumber: string;
  paymentMethod?: string;
  paymentMethodId?: string;
  actorId: string;
  actorName: string;
  supplierOrgId?: string;
  paymentTerms?: SupplierPaymentTermsSpec;
}): {
  source: 'purchase_order';
  status: 'pending_review';
  category: SupplierBillCategory;
  vendorName: string;
  description: string;
  amount: number;
  supplierOrgId?: string;
  rfqProjectId?: string;
  rfqSubmissionId?: string;
  purchaseRequisitionId: string;
  poNumber: string;
  dealId?: string;
  projectId?: string;
  plBucket?: PurchaseRequisition['plBucket'];
  paymentTerms?: SupplierPaymentTermsSpec;
  fileUrl?: string;
  storagePath?: string;
  fileName?: string;
  byUserId: string;
  byUserName: string;
} | null {
  const method = {
    id: args.paymentMethodId || args.requisition.paymentMethodId,
    label: args.paymentMethod || args.requisition.paymentMethod,
  };
  if (!shouldCreatePayableForIssuedPurchase(method)) return null;

  const quoteFile = (args.requisition.attachments || []).find(
    file => file.kind === 'quote' || !file.kind,
  );

  return {
    source: 'purchase_order',
    status: 'pending_review',
    category: billCategoryForJobPlBucket(args.requisition.plBucket || args.requisition.costObject.plBucket),
    vendorName: args.requisition.vendorName?.trim() || 'Supplier',
    description: args.requisition.title,
    amount: args.requisition.amount,
    supplierOrgId: args.supplierOrgId,
    rfqProjectId: args.requisition.rfqProjectId,
    rfqSubmissionId: args.requisition.rfqSubmissionId,
    purchaseRequisitionId: args.requisition.id,
    poNumber: args.poNumber,
    dealId: args.requisition.dealId || args.requisition.costObject.dealId,
    projectId: args.requisition.costObject.projectId,
    plBucket: args.requisition.plBucket || args.requisition.costObject.plBucket,
    paymentTerms: args.paymentTerms,
    fileUrl: quoteFile?.url,
    storagePath: quoteFile?.storagePath,
    fileName: quoteFile?.name,
    byUserId: args.actorId,
    byUserName: args.actorName,
  };
}

